import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const NavigationAnalytics = () => {
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    // Track page views and navigation patterns
    const trackNavigation = () => {
      const navigationData = {
        path: location.pathname,
        timestamp: Date.now(),
        userRole: user?.role,
        userId: user?.id,
        referrer: document.referrer,
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        }
      };

      // Store navigation data locally for analytics
      const existingData = JSON.parse(localStorage.getItem('navigationAnalytics') || '[]');
      existingData.push(navigationData);
      
      // Keep only last 100 navigation events
      const trimmedData = existingData.slice(-100);
      localStorage.setItem('navigationAnalytics', JSON.stringify(trimmedData));

      // Track common navigation patterns
      trackNavigationPatterns(trimmedData);
    };

    const trackNavigationPatterns = (data) => {
      if (data.length < 2) return;

      const recentData = data.slice(-10); // Last 10 navigations
      const patterns = {
        commonPaths: {},
        navigationFlow: [],
        timeSpent: {},
        bounceRate: 0,
        returnVisits: 0
      };

      // Analyze common paths
      recentData.forEach(item => {
        patterns.commonPaths[item.path] = (patterns.commonPaths[item.path] || 0) + 1;
      });

      // Analyze navigation flow
      for (let i = 1; i < recentData.length; i++) {
        const flow = `${recentData[i-1].path} → ${recentData[i].path}`;
        patterns.navigationFlow.push(flow);
      }

      // Calculate time spent on pages
      for (let i = 1; i < recentData.length; i++) {
        const currentPage = recentData[i-1].path;
        const timeSpent = recentData[i].timestamp - recentData[i-1].timestamp;
        
        if (!patterns.timeSpent[currentPage]) {
          patterns.timeSpent[currentPage] = [];
        }
        patterns.timeSpent[currentPage].push(timeSpent);
      }

      // Store patterns for potential optimization
      localStorage.setItem('navigationPatterns', JSON.stringify(patterns));
    };

    // Track performance metrics
    const trackPerformance = () => {
      if ('performance' in window) {
        const navigation = performance.getEntriesByType('navigation')[0];
        if (navigation) {
          const performanceData = {
            path: location.pathname,
            loadTime: navigation.loadEventEnd - navigation.loadEventStart,
            domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
            firstPaint: performance.getEntriesByName('first-paint')[0]?.startTime || 0,
            firstContentfulPaint: performance.getEntriesByName('first-contentful-paint')[0]?.startTime || 0,
            timestamp: Date.now()
          };

          const existingPerf = JSON.parse(localStorage.getItem('navigationPerformance') || '[]');
          existingPerf.push(performanceData);
          
          // Keep only last 50 performance entries
          const trimmedPerf = existingPerf.slice(-50);
          localStorage.setItem('navigationPerformance', JSON.stringify(trimmedPerf));
        }
      }
    };

    // Execute tracking
    trackNavigation();
    
    // Track performance after a short delay to ensure metrics are available
    setTimeout(trackPerformance, 1000);

  }, [location.pathname, user]);

  // Provide analytics data for debugging/optimization
  const getNavigationInsights = () => {
    const analytics = JSON.parse(localStorage.getItem('navigationAnalytics') || '[]');
    const patterns = JSON.parse(localStorage.getItem('navigationPatterns') || '{}');
    const performance = JSON.parse(localStorage.getItem('navigationPerformance') || '[]');

    return {
      totalPageViews: analytics.length,
      uniquePages: [...new Set(analytics.map(item => item.path))].length,
      averageSessionLength: analytics.length > 1 ? 
        (analytics[analytics.length - 1].timestamp - analytics[0].timestamp) / analytics.length : 0,
      mostVisitedPages: Object.entries(patterns.commonPaths || {})
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5),
      averageLoadTime: performance.length > 0 ? 
        performance.reduce((sum, item) => sum + item.loadTime, 0) / performance.length : 0,
      slowestPages: performance
        .sort((a, b) => b.loadTime - a.loadTime)
        .slice(0, 3)
        .map(item => ({ path: item.path, loadTime: item.loadTime }))
    };
  };

  // Expose insights globally for development
  if (process.env.NODE_ENV === 'development') {
    window.getNavigationInsights = getNavigationInsights;
  }

  return null; // This component doesn't render anything
};

export default NavigationAnalytics;
